import {
  Box,
  Button,
  ButtonProps,
  ClickAwayListener,
  Popper,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type DateRange = { start: string; end: string };

type RangeSide = 'start' | 'end';

/** Count how many unique calendar months the range spans (inclusive). */
export function countUniqueMonths(start: DateTime, end: DateTime): number {
  const orderedStart = start < end ? start : end;
  const orderedEnd = start < end ? end : start;
  return (
    (orderedEnd.year - orderedStart.year) * 12 +
    (orderedEnd.month - orderedStart.month) +
    1
  );
}

/** Clamp the range so it spans at most maxMonths unique months. */
export function clampRangeToMaxUniqueMonths(
  range: DateRange,
  maxMonths: number,
): DateRange {
  const start = DateTime.fromISO(range.start);
  const end = DateTime.fromISO(range.end);
  if (countUniqueMonths(start, end) <= maxMonths) return range;
  const lastAllowed = start.plus({ months: maxMonths - 1 }).endOf('month');
  const clampedEnd = end > lastAllowed ? lastAllowed : end;
  const finalEnd = clampedEnd < start ? start : clampedEnd;
  return {
    start: range.start,
    end: finalEnd.toISODate() ?? range.end,
  };
}

/** Clamp the range to stay within [bounds.start, bounds.end]. */
export function clampRangeToBounds(
  range: DateRange,
  bounds: AllowedRangeBounds,
): DateRange {
  const start = DateTime.fromISO(range.start);
  const end = DateTime.fromISO(range.end);
  const clampedStart =
    start < bounds.start ? bounds.start : start > bounds.end ? bounds.end : start;
  const clampedEnd =
    end > bounds.end ? bounds.end : end < bounds.start ? bounds.start : end;
  const orderedStart = clampedStart <= clampedEnd ? clampedStart : clampedEnd;
  const orderedEnd = clampedStart <= clampedEnd ? clampedEnd : clampedStart;
  return {
    start: orderedStart.toISODate() ?? range.start,
    end: orderedEnd.toISODate() ?? range.end,
  };
}

function intersectBounds(
  a: AllowedRangeBounds,
  b: AllowedRangeBounds,
): AllowedRangeBounds {
  const start = a.start > b.start ? a.start : b.start;
  const end = a.end < b.end ? a.end : b.end;
  return { start, end: start > end ? start : end };
}

/** When startDate and maxUniqueMonths are set, the allowed window for the range end. */
export type AllowedRangeBounds = { start: DateTime; end: DateTime };

type RangeDayProps = {
  startDate?: DateTime<true> | null;
  hoverDate?: DateTime<true> | null;
  onDateHover?: (date: DateTime<true>) => void;
  currentRange?: DateRange | null;
  /** Precomputed allowed [start, end] for the range; used by both calendars so the limit is on the full range. */
  allowedRangeBounds?: AllowedRangeBounds | null;
};

const leftRadius = { borderRadius: '50% 0 0 50%' };
const rightRadius = { borderRadius: '0 50% 50% 0' };
const fullRadius = { borderRadius: '50%' };

function RangeDay({
  startDate,
  hoverDate,
  onDateHover,
  currentRange,
  allowedRangeBounds,
  day,
  ...other
}: PickersDayProps<DateTime<true>> & RangeDayProps) {
  const outsideAllowedRange =
    allowedRangeBounds != null &&
    (day < allowedRangeBounds.start || day > allowedRangeBounds.end);

  // highlight day if:
  // - start date is selected and we are being hovered
  // - start date is selected and we are between the start date and the hover date
  // - currentRange is set and we are between the start and end date
  const highlight = useMemo(() => {
    const isSameDay = (a: DateTime, b: DateTime) =>
      a.toISODate() === b.toISODate();
    if (startDate) {
      if (!hoverDate) return isSameDay(day, startDate);
      if (isSameDay(hoverDate, startDate)) return true;
      if (hoverDate && hoverDate > startDate) {
        return day >= startDate && day <= hoverDate;
      }
      if (hoverDate && hoverDate < startDate) {
        return day <= startDate && day >= hoverDate;
      }
    }
    if (!startDate && currentRange) {
      return (
        day >= DateTime.fromISO(currentRange.start) &&
        day <= DateTime.fromISO(currentRange.end)
      );
    }
    return false;
  }, [startDate, hoverDate, currentRange, day]);

  // remove rounded corners from all days except the start and end
  const borderRadius = useMemo(() => {
    if (startDate) {
      if (hoverDate && startDate.toISODate() === hoverDate.toISODate()) {
        // we are hovering on the selected start date
        return fullRadius;
      }
      if (day.toISODate() === startDate.toISODate()) {
        // left or right edge
        const isStart = !hoverDate || day.toISODate() < hoverDate.toISODate();
        return isStart ? leftRadius : rightRadius;
      }
      if (hoverDate && day.toISODate() === hoverDate.toISODate()) {
        // left or right edge
        const isStart = day.toISODate() < startDate.toISODate();
        return isStart ? leftRadius : rightRadius;
      }
    }
    if (!startDate && currentRange) {
      if (currentRange.start === currentRange.end) {
        // selected range is a single day
        return fullRadius;
      }
      if (day.toISODate() === currentRange.start) {
        // left edge
        return leftRadius;
      }
      if (day.toISODate() === currentRange.end) {
        // right edge
        return rightRadius;
      }
    }

    // between start and end
    return { borderRadius: '0' };
  }, [currentRange, day, startDate, hoverDate]);

  return (
    <PickersDay
      {...other}
      sx={{ borderRadius, boxSizing: 'content-box', margin: 0, px: '2px' }}
      day={day}
      selected={highlight}
      disabled={outsideAllowedRange || other.disabled}
      onMouseOver={() => {
        onDateHover?.(day);
      }}
    />
  );
}

interface RangeCalendarCoreProps {
  side: RangeSide;
  visibleMonth: DateTime<true>;
  startDate: DateTime<true> | null;
  setStartDate: (date: DateTime<true> | null) => void;
  updateVisibleMonth: (newDate: DateTime, dateChanged: RangeSide) => void;
  hoverDate: DateTime<true> | null;
  setHoverDate: (date: DateTime<true> | null) => void;
  currentRange: DateRange | null;
  setRange: (range: DateRange) => void;
  /** Precomputed allowed [start, end] for the full range; same for both calendars. */
  allowedRangeBounds?: AllowedRangeBounds | null;
  /** Used when clamping the range on second-date pick. */
  maxUniqueMonths?: number;
}

export function RangeCalendarCore({
  side,
  visibleMonth,
  startDate,
  setStartDate,
  updateVisibleMonth,
  hoverDate,
  setHoverDate,
  currentRange,
  setRange,
  allowedRangeBounds,
  maxUniqueMonths,
}: RangeCalendarCoreProps) {
  // We assign these here to get type safety, as we have to cast this to `any`
  // down below
  const daySlotProps: RangeDayProps = {
    startDate,
    hoverDate,
    onDateHover: (date: DateTime<true>) => {
      setHoverDate(date);
    },
    currentRange,
    allowedRangeBounds,
  };
  return (
    <DateCalendar
      value={visibleMonth}
      views={['day']}
      onChange={(date: DateTime<true>) => {
        if (!startDate) {
          setStartDate(date);
        } else {
          const start = date > startDate ? startDate : date;
          const end = date > startDate ? date : startDate;
          let rangeToSet: DateRange = {
            start: start.toISODate(),
            end: end.toISODate(),
          };
          if (maxUniqueMonths != null) {
            rangeToSet = clampRangeToMaxUniqueMonths(
              rangeToSet,
              maxUniqueMonths,
            );
          }
          if (allowedRangeBounds != null) {
            rangeToSet = clampRangeToBounds(rangeToSet, allowedRangeBounds);
          }
          setRange(rangeToSet);
          setStartDate(null);
        }
      }}
      onMonthChange={(month) => {
        updateVisibleMonth(month, side);
      }}
      onYearChange={(year) => {
        updateVisibleMonth(year, side);
      }}
      slots={{
        day: RangeDay,
      }}
      slotProps={{
        // this is how the MUI demo shows to pass extra props to our custom day component
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        day: daySlotProps as any,
      }}
    />
  );
}

/**
 * InternalDate Range Picker using MUI
 * Note there is some confusing stuff going on here because MUI's `<DateCalendar>` component
 * doesn't easily allow syncing 2 separate calendar views. Most likely because they have their
 * own DateRangeCalendar that you have to pay for
 *
 * - The calendar view can only be changed programmatically by changing the calendar value (date selected)
 * - We have to override the day component to highlight if it's within the range
 * - We use the `range` state below as the actual value
 * - I had to disable the year selector as I couldn't find a way around it changing the selected value,
 *   thus setting the range before the user actually wants to
 */
export function RangeCalendar({
  value,
  label,
  labelProps,
  onChange,
  maxUniqueMonths,
  minDate,
  maxDate,
}: {
  value: DateRange | null;
  label: string;
  labelProps?: ButtonProps;
  onChange?: (dateRange: DateRange | null) => void;
  /** When set, the selected range may span at most this many unique calendar months. */
  maxUniqueMonths?: number;
  /** When set, the selected range cannot start before this date (ISO). */
  minDate?: string;
  /** When set, the selected range cannot end after this date (ISO). */
  maxDate?: string;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [range, setRange] = useState<DateRange | null>(value ?? null);
  const [startDate, setStartDate] = useState<DateTime<true> | null>(null);
  const [hoverDate, setHoverDate] = useState<DateTime | null>(null);

  const date = value ? DateTime.fromISO(value.start) : DateTime.now();
  const [visibleMonths, setVisibleMonths] = useState<[DateTime, DateTime]>([
    date,
    date.plus({ month: 1 }),
  ]);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const baseBounds: AllowedRangeBounds | null = useMemo(() => {
    if (!minDate || !maxDate) return null;
    const start = DateTime.fromISO(minDate);
    const end = DateTime.fromISO(maxDate);
    if (!start.isValid || !end.isValid) return null;
    return { start, end: start > end ? start : end };
  }, [minDate, maxDate]);

  // Single allowed window for the full range (start→end), so both calendars use the same bounds
  const allowedRangeBounds: AllowedRangeBounds | null = useMemo(() => {
    const monthBounds: AllowedRangeBounds | null =
      startDate != null && maxUniqueMonths != null
        ? {
            start: startDate
              .minus({ months: maxUniqueMonths - 1 })
              .startOf('month'),
            end: startDate.plus({ months: maxUniqueMonths - 1 }).endOf('month'),
          }
        : null;
    if (baseBounds == null) return monthBounds;
    if (monthBounds == null) return baseBounds;
    return intersectBounds(baseBounds, monthBounds);
  }, [baseBounds, startDate, maxUniqueMonths]);

  const updateVisibleMonth = (
    newDate: DateTime,
    dateChanged: 'start' | 'end',
  ) => {
    // if the start date was changed, update visibleMonth to the start date
    // if end date was changed, change start date to the previous month
    const newStartDate =
      dateChanged === 'start' ? newDate : newDate.minus({ month: 1 });
    setVisibleMonths([newStartDate, newStartDate.plus({ month: 1 })]);
  };

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <div>
        <Button
          variant="text"
          {...labelProps}
          aria-describedby={id}
          type="button"
          onClick={(event) => {
            if (!anchorEl) {
              // Sync internal state with current value each time the picker opens
              setRange(value ?? null);
              setStartDate(null);
              setHoverDate(null);
              const d = value ? DateTime.fromISO(value.start) : DateTime.now();
              const base = d.isValid ? d : DateTime.now();
              setVisibleMonths([base, base.plus({ months: 1 })]);
            }
            setAnchorEl(anchorEl ? null : event.currentTarget);
          }}
        >
          {label}
        </Button>
        <Popper id={id} open={open} anchorEl={anchorEl} className="z-20">
          <Box sx={{ p: 1, bgcolor: 'background.paper' }} className="shadow-md">
            <div className="flex">
              <RangeCalendarCore
                key="start"
                side="start"
                visibleMonth={visibleMonths[0]}
                startDate={startDate}
                setStartDate={setStartDate}
                currentRange={range}
                hoverDate={hoverDate}
                setHoverDate={setHoverDate}
                setRange={setRange}
                updateVisibleMonth={updateVisibleMonth}
                allowedRangeBounds={allowedRangeBounds}
                maxUniqueMonths={maxUniqueMonths}
              />
              <RangeCalendarCore
                key="end"
                side="end"
                visibleMonth={visibleMonths[1]}
                startDate={startDate}
                setStartDate={setStartDate}
                currentRange={range}
                hoverDate={hoverDate}
                setHoverDate={setHoverDate}
                setRange={setRange}
                updateVisibleMonth={updateVisibleMonth}
                allowedRangeBounds={allowedRangeBounds}
                maxUniqueMonths={maxUniqueMonths}
              />
            </div>
            <div className="border-t border-light-200" />
            <div className="flex justify-end p-2">
              <Button
                variant="outlined"
                sx={{ mr: 2 }}
                onClick={() => {
                  setRange(value);
                  setAnchorEl(null);
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={() => {
                  setAnchorEl(null);
                  onChange?.(range?.start && range?.end ? range : null);
                }}
                className="ml-1"
              >
                {t('apply')}
              </Button>
            </div>
          </Box>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
